import { useEffect, useMemo, useRef, useState } from "react";
import type {
    PickerItemData,
    PickerStatus,
} from "@design-system/Picker/Picker.types";
import type { PickerListRootProps } from "@design-system/Picker/PickerRoot.types";
import type { PickerPlaygroundFeatures } from "./PickerPlayground.lib";
import {
    buildCompanyItems,
    COMPANY_LATENCY_MS,
    COMPANY_PAGE_SIZE,
    searchCompanies,
} from "./PickerPlaygroundCompanies";

const SEARCH_DEBOUNCE_MS = 300;

/** What the paged stage reports into the sandbox JSON output. */
export interface PickerPlaygroundPaging {
    query: string;
    loadedRows: number;
    /** Rows matching `query`; `null` until its first page lands. */
    total: number | null;
    pagesRequested: number;
    lastLoadStatus: "loading" | "loaded" | "failed" | null;
}

/** The data half of the stage root: static rows, or the paged source's. */
export type PickerPlaygroundListData = Pick<
    PickerListRootProps<string>,
    | "items"
    | "sections"
    | "status"
    | "hasMore"
    | "onLoadMore"
    | "loadMoreStatus"
    | "onInputValueChange"
    | "onOpenChange"
>;

interface PagedState {
    query: string;
    rows: Array<PickerItemData<string>>;
    total: number | null;
    status: PickerStatus;
    loadMoreStatus: PickerStatus;
    pagesRequested: number;
    lastLoadStatus: PickerPlaygroundPaging["lastLoadStatus"];
}

const initialPagedState: PagedState = {
    query: "",
    rows: [],
    total: null,
    status: "idle",
    loadMoreStatus: "idle",
    pagesRequested: 0,
    lastLoadStatus: null,
};

/**
 * A fake server for the paged stage: the first page loads on open, search
 * is debounced and runs on the "server", and each next page arrives after
 * a delay (or fails while `isNextPageFailing`, until the error row's "Try
 * again" asks once the toggle is off). Plain state and timeouts;
 * stale requests are cancelled on a new query and on unmount.
 */
export const usePickerPlaygroundPagedItems = ({
    features,
    onPagingChange,
}: {
    features: PickerPlaygroundFeatures;
    onPagingChange: (paging: PickerPlaygroundPaging) => void;
}): PickerPlaygroundListData => {
    const { hasDescriptions, hasRightText } = features;
    const companies = useMemo(
        () => buildCompanyItems({ hasDescriptions, hasRightText }),
        [hasDescriptions, hasRightText],
    );
    const [state, setState] = useState(initialPagedState);
    const stateRef = useRef(initialPagedState);
    const isOpenRef = useRef(false);
    const timeoutsRef = useRef<{ page?: number; search?: number }>({});

    useEffect(() => {
        const timeouts = timeoutsRef.current;
        return () => {
            window.clearTimeout(timeouts.page);
            window.clearTimeout(timeouts.search);
        };
    }, []);

    const commit = (next: PagedState) => {
        stateRef.current = next;
        setState(next);
        onPagingChange({
            query: next.query,
            loadedRows: next.rows.length,
            total: next.total,
            pagesRequested: next.pagesRequested,
            lastLoadStatus: next.lastLoadStatus,
        });
    };

    const schedulePage = (settle: () => void) => {
        window.clearTimeout(timeoutsRef.current.page);
        timeoutsRef.current.page = window.setTimeout(
            settle,
            COMPANY_LATENCY_MS,
        );
    };

    const settlePage = ({
        query,
        offset,
    }: {
        query: string;
        offset: number;
    }) => {
        const matches = searchCompanies(companies, query);
        const current = stateRef.current;
        commit({
            ...current,
            rows: [
                ...current.rows,
                ...matches.slice(offset, offset + COMPANY_PAGE_SIZE),
            ],
            total: matches.length,
            status: "idle",
            loadMoreStatus: "idle",
            lastLoadStatus: "loaded",
        });
    };

    const requestFirstPage = (query: string) => {
        const current = stateRef.current;
        commit({
            ...current,
            query,
            rows: [],
            total: null,
            status: "loading",
            loadMoreStatus: "idle",
            pagesRequested: current.pagesRequested + 1,
            lastLoadStatus: "loading",
        });
        schedulePage(() => settlePage({ query, offset: 0 }));
    };

    const requestNextPage = () => {
        const current = stateRef.current;
        const shouldFail = features.isNextPageFailing;
        commit({
            ...current,
            loadMoreStatus: "loading",
            pagesRequested: current.pagesRequested + 1,
            lastLoadStatus: "loading",
        });
        schedulePage(() => {
            if (shouldFail) {
                commit({
                    ...stateRef.current,
                    loadMoreStatus: "error",
                    lastLoadStatus: "failed",
                });
                return;
            }
            settlePage({ query: current.query, offset: current.rows.length });
        });
    };

    // Every open starts from the unfiltered list, like a cleared search; the
    // text an input trigger syncs back on close never becomes a query.
    const handleOpenChange = (isOpen: boolean) => {
        isOpenRef.current = isOpen;
        if (!isOpen) {
            window.clearTimeout(timeoutsRef.current.search);
            return;
        }
        const current = stateRef.current;
        if (current.pagesRequested === 0 || current.query !== "") {
            requestFirstPage("");
        }
    };

    const handleInputValueChange = (typed: string) => {
        const query = typed.trim();
        window.clearTimeout(timeoutsRef.current.search);
        timeoutsRef.current.search = window.setTimeout(() => {
            if (isOpenRef.current && query !== stateRef.current.query) {
                requestFirstPage(query);
            }
        }, SEARCH_DEBOUNCE_MS);
    };

    return {
        items: state.rows,
        status: state.status,
        hasMore:
            state.status === "idle" &&
            state.total !== null &&
            state.rows.length < state.total,
        onLoadMore: requestNextPage,
        loadMoreStatus: state.loadMoreStatus,
        onInputValueChange: handleInputValueChange,
        onOpenChange: handleOpenChange,
    };
};
